/**
 * Client for the Snap ("Guerilla") campaign admin API.
 *
 * In deployed environments requests go through the same-origin Vercel proxy
 * (`/api/snap`, see src/server/snapProxy.ts) which injects the shared bot key
 * server-side. For local `vite dev` (no /api functions) set `VITE_SNAP_API_URL`
 * (+ `VITE_SNAP_API_KEY`) to talk to a bot directly.
 *
 * The shape mirrors the Te Pone admin code (the campaigns share an identity
 * spine) plus two Snap-specific fields the booth crew care about: `booth` (which
 * pop-up minted the code) and `has_consent` (GDPR capture at the booth).
 */

export type SnapAdminCode = {
  token: string;
  code: string;
  status:
    | "minted"
    | "opened"
    | "locked"
    | "ready"
    | "claimed"
    | "delivered"
    | "expired"
    | "void"
    | string;
  /** Internal dev code (dev-* batch): never expires, resets after delivery. */
  dev?: boolean;
  batch: string | null;
  event_label: string | null;
  /** The pop-up / location this batch was minted for. */
  booth: string | null;
  narration_language: string | null;
  wa_id: string | null;
  workload_id: string | null;
  media_count: number;
  /** Booth recorded GDPR consent before binding the photo. */
  has_consent: boolean;
  created_at: string | null;
  opened_at: string | null;
  ready_at: string | null;
  claimed_at: string | null;
  delivered_at: string | null;
  expires_at: string | null;
  portal_url: string;
  /** What the QR encodes: the scan-tracker redirect (records the scan, then 302s to wa.me). */
  wa_link: string;
  /** Explicit alias of the QR/tracker URL. */
  qr_url?: string;
  /** The raw wa.me link — use for admin "Open WhatsApp" so clicks don't count as scans. */
  wa_direct?: string;
  /**
   * Number of guest QR scans recorded for this code (debounced; total hits).
   * 0 for codes that predate scan tracking. Distinct-scanned (`scan_count >= 1`)
   * is the funnel-meaningful signal.
   */
  scan_count?: number;
  /** First guest scan timestamp; null if never scanned. */
  first_scanned_at?: string | null;
};

/** One stage in the bot's token-keyed pre-WhatsApp funnel. */
export type SnapFunnelStage = {
  key: string;
  label: string;
  count: number;
};

/**
 * Aggregate snap funnel from the bot (`GET /snap/v1/admin/funnel`). This is the
 * token-keyed, pre-WhatsApp source (scans live here, not in the wa_id-keyed
 * dashboard funnel). `tracking_since` is null when no scans have been recorded.
 */
export type SnapFunnel = {
  params: { range: string; window_hours: number; booth?: string | null };
  tracking_since: string | null;
  cohort_size: number;
  stages: SnapFunnelStage[];
  scanned_not_messaged: number;
  scanned_not_messaged_settled: number;
  incomplete_cohort: boolean;
};

function apiTarget(): { base: string; headers: Record<string, string> } {
  const direct = import.meta.env.VITE_SNAP_API_URL as string | undefined;
  if (direct) {
    const key = import.meta.env.VITE_SNAP_API_KEY as string | undefined;
    return {
      base: `${direct.replace(/\/$/, "")}/snap/v1`,
      headers: key ? { "X-Snap-Key": key } : {},
    };
  }
  const proxy =
    (import.meta.env.VITE_SNAP_PROXY_PATH as string | undefined) || "/api/snap";
  return { base: proxy.replace(/\/$/, ""), headers: {} };
}

async function snapRequest<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const { base, headers } = apiTarget();
  const response = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...headers,
      ...(init.headers || {}),
    },
  });
  const text = await response.text();
  let payload: any = null;
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    payload = null;
  }
  if (!response.ok) {
    throw new Error(payload?.error || `Snap API error ${response.status}`);
  }
  if (payload === null) {
    // An HTML body with a 200 means the SPA catch-all swallowed the proxy
    // route (vercel.json rewrite missing) — surface it instead of rendering
    // an empty list.
    throw new Error("Snap API returned a non-JSON response (proxy misrouted)");
  }
  return payload as T;
}

export async function listGuerillaCodes(limit = 500): Promise<SnapAdminCode[]> {
  const { codes } = await snapRequest<{ codes: SnapAdminCode[] }>(
    `/admin/codes?limit=${limit}`,
  );
  return codes ?? [];
}

/**
 * Fetch the bot's pre-WhatsApp snap funnel for a given CRM range. The bot
 * aligns its ranges to the CRM's (`this_week`/`30d`/`90d`/`all`); pass the same
 * range the dashboard funnel uses so the two windows line up.
 */
export async function fetchSnapFunnel(range: string): Promise<SnapFunnel> {
  return snapRequest<SnapFunnel>(
    `/admin/funnel?range=${encodeURIComponent(range)}`,
  );
}

export type SnapCodeImage = {
  url: string;
  content_type: string | null;
};

/**
 * Short-lived SIGNED read URLs for the photo(s) bound to a code.
 *
 * The bot returns presigned GETs (expire within ~1h) over the private quarantine
 * objects — never public links. Fetch these lazily, only when the operator opens
 * the viewer, so we don't mint signed URLs for codes nobody looks at (and so the
 * URL is always fresh when shown).
 */
export async function getGuerillaCodeImages(
  token: string,
): Promise<SnapCodeImage[]> {
  const { images } = await snapRequest<{ images: SnapCodeImage[] }>(
    `/admin/codes/${encodeURIComponent(token)}/image`,
  );
  return images ?? [];
}

export async function mintGuerillaCodes(options?: {
  booth?: string;
  count?: number;
  dev?: boolean;
}): Promise<SnapAdminCode[]> {
  const body: Record<string, unknown> = {};
  if (options?.booth) body.booth = options.booth;
  if (options?.count) body.count = options.count;
  // Internal dev link: never expires + resets to fresh after each delivery,
  // so the same link runs the whole real flow repeatedly.
  if (options?.dev) body.dev = true;
  const { codes } = await snapRequest<{ codes: SnapAdminCode[] }>(
    "/admin/codes",
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  return codes ?? [];
}
